import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useAktivitaetModal } from '../components/AktivitaetModalProvider.jsx';
import TelefonLink from '../components/TelefonLink.jsx';
import SelektionFormModal from '../components/SelektionFormModal.jsx';
import { applyFilterRegel, beschreibeFilterRegel } from '../lib/praxisFilter.js';
import {
  formatDatum,
  heuteAlsInputValue,
  tageSeit,
  userAnzeige,
} from '../lib/aktivitaet.js';
import { FACHRICHTUNG_LABEL, fachgebietAnzeige } from '../lib/fachrichtung.js';
import {
  PRAXIS_ART_LABEL,
  VERSORGUNGSFORM_LABEL,
  VERBUND_LABEL,
  praxisAnzeige,
  PRAXIS_STATUS_LABEL,
  PRAXIS_TYP_LABEL,
} from '../lib/praxis.js';

const TAB_OFFEN = 'offen';
const TAB_ERLEDIGT = 'erledigt';
const TAB_FILTER = 'filter';

const SORT_NAME = 'name';
const SORT_ORT = 'ort';
const SORT_LAST_KONTAKT = 'last_kontakt';

const SAMMLER_DE = new Intl.Collator('de', { sensitivity: 'base' });

function vergleicheNachKey(a, b, key, dir) {
  let av;
  let bv;
  if (key === SORT_LAST_KONTAKT) {
    av = a.last_kontakt ?? null;
    bv = b.last_kontakt ?? null;
    // Null/leer immer ans Ende — egal ob asc oder desc.
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    // ISO-Strings sortieren lexikographisch korrekt nach Zeit.
    if (av === bv) return 0;
    return dir === 'asc' ? (av < bv ? -1 : 1) : av < bv ? 1 : -1;
  }
  if (key === SORT_ORT) {
    // Spalte zeigt "PLZ Ort" — Sortierung nach PLZ ist für Touren-Planung
    // brauchbarer als alphabetisch nach Ortsname. Dt. PLZen sind 5-stellige
    // Strings, lexikografische Sortierung entspricht hier der numerischen.
    av = a.plz || '';
    bv = b.plz || '';
    if (!av && !bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;
    if (av === bv) return 0;
    return dir === 'asc' ? (av < bv ? -1 : 1) : av < bv ? 1 : -1;
  }
  av = a.name || '';
  bv = b.name || '';
  // Leere Strings immer ans Ende — analog zu null bei Datum.
  if (!av && !bv) return 0;
  if (!av) return 1;
  if (!bv) return -1;
  const cmp = SAMMLER_DE.compare(av, bv);
  return dir === 'asc' ? cmp : -cmp;
}

function SortPfeil({ active, dir }) {
  if (!active) return null;
  return <span className="ml-1 text-slate-400">{dir === 'asc' ? '▲' : '▼'}</span>;
}

function PraxisZeile({ praxis, rechts, selektionId }) {
  const fach = fachgebietAnzeige(praxis);
  const ort = [praxis.plz, praxis.ort].filter(Boolean).join(' ');
  const tage = tageSeit(praxis.last_kontakt);
  // Selektions-Kontext via Query-Param mitgeben — die Detailseite zeigt
  // daraufhin ein Banner und springt nach dem Speichern einer neuen
  // Aktivität automatisch zurück (Power-Dialer-Flow).
  const praxisLink = selektionId
    ? `/praxen/${praxis.id}?selektion=${selektionId}`
    : `/praxen/${praxis.id}`;
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3">
        <Link
          to={praxisLink}
          className="text-sm font-semibold text-slate-900 hover:text-primary-700 hover:underline"
        >
          {praxisAnzeige(praxis)}
        </Link>
        {fach && <div className="mt-0.5 text-xs text-slate-500">{fach}</div>}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        {ort || <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 text-sm">
        <TelefonLink phone={praxis.telefon} praxisId={praxis.id} />
      </td>
      <td className="px-4 py-3 text-sm">
        {tage === null ? (
          <span className="text-slate-400">Noch kein Kontakt</span>
        ) : (
          <span className={tage > 30 ? 'font-medium text-red-600' : 'text-slate-700'}>
            {formatDatum(praxis.last_kontakt)}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">{rechts}</td>
    </tr>
  );
}

function SortHeader({ children, sortKey, currentKey, currentDir, onSort }) {
  const active = sortKey === currentKey;
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center hover:text-slate-700"
      >
        {children}
        <SortPfeil active={active} dir={currentDir} />
      </button>
    </th>
  );
}

function TableKopf({ sortKey, sortDir, onSort }) {
  return (
    <thead className="bg-slate-50">
      <tr>
        <SortHeader
          sortKey={SORT_NAME}
          currentKey={sortKey}
          currentDir={sortDir}
          onSort={onSort}
        >
          Name / Fachgebiet
        </SortHeader>
        <SortHeader
          sortKey={SORT_ORT}
          currentKey={sortKey}
          currentDir={sortDir}
          onSort={onSort}
        >
          Ort
        </SortHeader>
        <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
          Telefon
        </th>
        <SortHeader
          sortKey={SORT_LAST_KONTAKT}
          currentKey={sortKey}
          currentDir={sortDir}
          onSort={onSort}
        >
          Letzter Kontakt
        </SortHeader>
        <th className="px-4 py-2" />
      </tr>
    </thead>
  );
}

function TabButton({ active, onClick, label, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition ${
        active
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {label}
      {count != null && (
        <span
          className={`inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs ${
            active ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-600'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export default function SelektionDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openCreate } = useAktivitaetModal();

  const [selektion, setSelektion] = useState(null);
  const [praxen, setPraxen] = useState([]);
  const [erledigtEintraege, setErledigtEintraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_OFFEN);
  const [editOpen, setEditOpen] = useState(false);
  const [aktionFehler, setAktionFehler] = useState(null);
  const [erledigtToast, setErledigtToast] = useState(null);
  const [sortKey, setSortKey] = useState(SORT_NAME);
  const [sortDir, setSortDir] = useState('asc');

  function handleSort(nextKey) {
    if (nextKey === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(nextKey);
      setSortDir('asc');
    }
  }

  const istErsteller = selektion?.erstellt_von === user?.id;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sel, praxenListe, aktivitaeten, erledigtListe] = await Promise.all([
        pb.collection('selektionen').getOne(id, { expand: 'erstellt_von' }),
        pb.collection('praxen').getFullList({ sort: 'name' }),
        pb.collection('aktivitaeten').getFullList({
          fields: 'praxis,faellig_am',
          sort: '-faellig_am',
        }),
        pb.collection('selektion_erledigt').getFullList({
          filter: `selektion = "${id}"`,
          expand: 'erledigt_von',
          sort: '-erledigt_am',
        }),
      ]);
      const lastKontaktMap = new Map();
      for (const a of aktivitaeten) {
        if (!a.faellig_am) continue;
        if (!lastKontaktMap.has(a.praxis)) {
          lastKontaktMap.set(a.praxis, a.faellig_am);
        }
      }
      const annotated = praxenListe.map((p) => ({
        ...p,
        last_kontakt: lastKontaktMap.get(p.id) ?? null,
      }));
      setSelektion(sel);
      setPraxen(annotated);
      setErledigtEintraege(erledigtListe);
    } catch (err) {
      setError(err?.message || 'Selektion konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Power-Dialer-Rückkehr: PraxisDetailPage hängt nach dem Speichern einer
  // neuen Aktivität "?erledigt=<praxisId>" an die URL. Wenn die Selektion
  // fertig geladen ist, zeigen wir kurz einen Toast und räumen den Param
  // weg, damit er bei Reload nicht erneut feuert.
  useEffect(() => {
    const praxisId = searchParams.get('erledigt');
    if (!praxisId || loading || !selektion) return;
    setErledigtToast({
      praxisId,
      offen: offenePraxen.length,
      gesamt: matchingPraxen.length,
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('erledigt');
        return next;
      },
      { replace: true }
    );
    const timer = setTimeout(() => setErledigtToast(null), 3000);
    return () => clearTimeout(timer);
    // offenePraxen / matchingPraxen extra absichtlich NICHT in den deps —
    // sonst würde der Toast bei jeder Listenmutation neu starten. Wir
    // wollen genau eine Anzeige pro Param-Auftritt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, selektion]);

  const matchingPraxen = useMemo(() => {
    if (!selektion) return [];
    return applyFilterRegel(praxen, selektion.filter_regel);
  }, [selektion, praxen]);

  // Map: praxisId → erledigt-Eintrag. Für O(1)-Lookup beim Aufteilen
  // in Offen vs. Erledigt und beim "Wieder öffnen".
  const erledigtByPraxis = useMemo(() => {
    const map = new Map();
    for (const e of erledigtEintraege) {
      map.set(e.praxis, e);
    }
    return map;
  }, [erledigtEintraege]);

  const offenePraxen = useMemo(() => {
    const liste = matchingPraxen.filter((p) => !erledigtByPraxis.has(p.id));
    return [...liste].sort((a, b) => vergleicheNachKey(a, b, sortKey, sortDir));
  }, [matchingPraxen, erledigtByPraxis, sortKey, sortDir]);

  // Erledigte: anhand der Erledigt-Liste, nicht via matchingPraxen — eine
  // Praxis bleibt erledigt, auch wenn sie heute nicht mehr zum Filter passt.
  const praxenById = useMemo(() => {
    const map = new Map();
    for (const p of praxen) map.set(p.id, p);
    return map;
  }, [praxen]);

  const erledigtMitPraxis = useMemo(() => {
    const liste = erledigtEintraege
      .map((e) => {
        const praxis = praxenById.get(e.praxis);
        if (!praxis) return null;
        return { eintrag: e, praxis };
      })
      .filter(Boolean);
    return [...liste].sort((a, b) =>
      vergleicheNachKey(a.praxis, b.praxis, sortKey, sortDir)
    );
  }, [erledigtEintraege, praxenById, sortKey, sortDir]);

  async function handleAbhaken(praxis) {
    setAktionFehler(null);
    // Aktivität anlegen, und im onSaved-Callback den Erledigt-Eintrag.
    // Bricht der User das Modal ab, feuert onSaved nicht — die Selektion
    // bleibt unberührt.
    await openCreate({
      praxisId: praxis.id,
      onSaved: async (savedAktivitaet) => {
        if (!savedAktivitaet?.id) return;
        try {
          const eintrag = await pb.collection('selektion_erledigt').create({
            selektion: id,
            praxis: praxis.id,
            erledigt_von: user?.id,
            erledigt_am: heuteAlsInputValue(),
            aktivitaet: savedAktivitaet.id,
          });
          // Mit Expand nachladen, damit die Erledigt-Tabelle direkt den
          // User-Namen anzeigen kann, ohne die ganze Liste neu zu fetchen.
          const mitExpand = await pb
            .collection('selektion_erledigt')
            .getOne(eintrag.id, { expand: 'erledigt_von' });
          setErledigtEintraege((prev) => [mitExpand, ...prev]);
        } catch (err) {
          setAktionFehler(
            err?.message ||
              'Erledigt-Markierung konnte nicht gespeichert werden — die Aktivität wurde trotzdem angelegt.'
          );
        }
      },
    });
  }

  async function handleWiederOeffnen(eintrag) {
    setAktionFehler(null);
    try {
      await pb.collection('selektion_erledigt').delete(eintrag.id);
      setErledigtEintraege((prev) => prev.filter((e) => e.id !== eintrag.id));
    } catch (err) {
      setAktionFehler(err?.message || 'Zurücksetzen fehlgeschlagen.');
    }
  }

  async function handleArchiv() {
    if (!istErsteller) return;
    if (
      !window.confirm(
        `Selektion "${selektion.name}" archivieren? Sie verschwindet aus der Übersicht, bleibt aber inkl. Erledigt-Spur erhalten.`
      )
    ) {
      return;
    }
    try {
      await pb.collection('selektionen').update(selektion.id, { aktiv: false });
      navigate('/selektionen');
    } catch (err) {
      setAktionFehler(err?.message || 'Archivieren fehlgeschlagen.');
    }
  }

  if (loading) {
    return <div className="card text-sm text-slate-500">Lade …</div>;
  }
  if (error) {
    return (
      <div className="space-y-3">
        <Link to="/selektionen" className="text-sm text-primary-600 hover:underline">
          ← Zurück zur Übersicht
        </Link>
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      </div>
    );
  }
  if (!selektion) return null;

  const ersteller = selektion.expand?.erstellt_von;
  const filterZeilen = beschreibeFilterRegel(selektion.filter_regel, {
    statusLabel: (s) => PRAXIS_STATUS_LABEL[s] ?? s,
    praxisArtLabel: (s) => PRAXIS_ART_LABEL[s] ?? s,
    versorgungsformLabel: (s) => VERSORGUNGSFORM_LABEL[s] ?? s,
    verbundLabel: (s) => (s === 'keiner' ? 'Kein Verbund' : VERBUND_LABEL[s] ?? s),
    typLabel: (s) => PRAXIS_TYP_LABEL[s] ?? s,
    fachgebietLabel: (s) => FACHRICHTUNG_LABEL[s] ?? s,
  });

  return (
    <div className="space-y-5">
      <div>
        <Link to="/selektionen" className="text-sm text-primary-600 hover:underline">
          ← Zurück zur Übersicht
        </Link>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{selektion.name}</h1>
          <p className="mt-1 text-xs text-slate-500">
            Erstellt von {istErsteller ? 'dir' : userAnzeige(ersteller)}
          </p>
          {selektion.beschreibung && (
            <div
              className="prose prose-sm mt-3 max-w-3xl text-sm text-slate-700"
              dangerouslySetInnerHTML={{ __html: selektion.beschreibung }}
            />
          )}
        </div>
        {istErsteller && (
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setEditOpen(true)}
            >
              Bearbeiten
            </button>
            <button
              type="button"
              className="btn-secondary text-red-700 hover:bg-red-50"
              onClick={handleArchiv}
            >
              Archivieren
            </button>
          </div>
        )}
      </div>

      {aktionFehler && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {aktionFehler}
        </div>
      )}

      <div className="card space-y-4">
        <div className="-mx-6 -mt-5 border-b border-slate-200 px-6">
          <nav className="flex gap-2">
            <TabButton
              active={activeTab === TAB_OFFEN}
              onClick={() => setActiveTab(TAB_OFFEN)}
              label="Offen"
              count={offenePraxen.length}
            />
            <TabButton
              active={activeTab === TAB_ERLEDIGT}
              onClick={() => setActiveTab(TAB_ERLEDIGT)}
              label="Erledigt"
              count={erledigtMitPraxis.length}
            />
            <TabButton
              active={activeTab === TAB_FILTER}
              onClick={() => setActiveTab(TAB_FILTER)}
              label="Filter"
            />
          </nav>
        </div>

        {activeTab === TAB_OFFEN && (
          <div className="overflow-x-auto">
            {offenePraxen.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                {matchingPraxen.length === 0
                  ? 'Keine Praxen passen aktuell zum Filter.'
                  : 'Alles abgehakt — keine offenen Praxen mehr.'}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <TableKopf sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <tbody className="divide-y divide-slate-200 bg-white">
                  {offenePraxen.map((praxis) => (
                    <PraxisZeile
                      key={praxis.id}
                      praxis={praxis}
                      selektionId={id}
                      rechts={
                        <button
                          type="button"
                          className="btn-primary text-xs"
                          onClick={() => handleAbhaken(praxis)}
                        >
                          + Aktivität anlegen
                        </button>
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === TAB_ERLEDIGT && (
          <div className="overflow-x-auto">
            {erledigtMitPraxis.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                Noch nichts erledigt.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <TableKopf sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <tbody className="divide-y divide-slate-200 bg-white">
                  {erledigtMitPraxis.map(({ eintrag, praxis }) => (
                    <PraxisZeile
                      key={eintrag.id}
                      praxis={praxis}
                      selektionId={id}
                      rechts={
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-slate-500">
                            Erledigt {formatDatum(eintrag.erledigt_am)} von{' '}
                            {userAnzeige(eintrag.expand?.erledigt_von)}
                          </span>
                          {istErsteller && (
                            <button
                              type="button"
                              className="text-xs font-medium text-primary-600 hover:text-primary-800"
                              onClick={() => handleWiederOeffnen(eintrag)}
                            >
                              Wieder öffnen
                            </button>
                          )}
                        </div>
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === TAB_FILTER && (
          <div className="space-y-3">
            {filterZeilen.length === 0 ? (
              <p className="text-sm text-slate-600">
                Kein Filter gesetzt — die Selektion umfasst{' '}
                <strong>alle</strong> Praxen.
              </p>
            ) : (
              <dl className="divide-y divide-slate-100">
                {filterZeilen.map((zeile) => (
                  <div
                    key={zeile.key}
                    className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-[8rem_1fr]"
                  >
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {zeile.label}
                    </dt>
                    <dd className="text-sm text-slate-700">{zeile.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            <div className="text-xs text-slate-500">
              Trifft aktuell auf {matchingPraxen.length} Praxen zu —{' '}
              {offenePraxen.length} offen, {erledigtMitPraxis.length} erledigt.
            </div>
            {istErsteller && (
              <div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditOpen(true)}
                >
                  Filter bearbeiten
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <SelektionFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => {
          // In Place aktualisieren — Filter-Tab und Counts werden direkt neu
          // gerechnet. Erledigt-Liste bleibt unberührt; Praxen, die heute
          // nicht mehr matchen aber abgehakt sind, bleiben im Erledigt-Tab.
          if (saved) setSelektion((prev) => ({ ...prev, ...saved }));
        }}
        selektion={selektion}
      />

      {erledigtToast && (
        <div
          className="fixed bottom-4 right-4 z-50 rounded-md bg-emerald-600 px-4 py-3 text-sm font-medium text-white shadow-lg ring-1 ring-emerald-700 transition-opacity duration-300"
          role="status"
        >
          Praxis erledigt — noch {erledigtToast.offen} von {erledigtToast.gesamt} offen.
        </div>
      )}
    </div>
  );
}
